// ============================================
// LIGA AJUPAM - Sesión persistida del staff de playoffs (v6.11.208)
// ============================================
// El corazón común de OpenerAuthProvider, ReceptionistAuthProvider y
// SecurityAuthProvider: token en localStorage + validación contra el servidor.
//
// Lo que cambió respecto de v6.8.0–v6.11.188, y por qué (playoffs 4ta edición):
//
// - VOLVER A LA APP YA NO DESMONTA LA HERRAMIENTA. Antes cada
//   `visibilitychange` (bloquear la pantalla, pasar a WhatsApp, volver de la
//   cámara nativa) ponía el proveedor en `isLoading`, y la ruta mostraba
//   "Validando sesión…" en lugar del centro de control: se perdían el modal de
//   resultado a medio tipear, los filtros y el estado de la pantalla, y al
//   volver había que esperar la cadena entera de pedidos. Ahora `isLoading` es
//   SÓLO el arranque; al volver se re-valida en silencio y a lo sumo cada
//   `STAFF_REVALIDATE_MIN_MS` (cada RPC igual valida el token en el servidor).
//
// - UN CORTE DE RED YA NO DESLOGUEA. Antes cualquier error de la validación
//   (timeout con el WiFi de la cancha incluido) borraba el token y había que
//   volver a tipear usuario y PIN. Ahora sólo un rechazo con SQLSTATE 28000
//   (`isStaffAuthError`) cierra la sesión; lo demás deja todo como estaba.
//
// - LOS DATOS DE LA SESIÓN SE GUARDAN. La validación del servidor no devuelve
//   slug/nombre de liga ni vencimiento: antes se perdían al recargar
//   (`leagueSlug: null`). Ahora se guardan junto al token y, si el arranque no
//   tiene red, la herramienta abre igual con lo guardado.

import { useCallback, useEffect, useRef, useState } from 'react';
import { isStaffAuthError, shouldRevalidateStaffSession } from '../utils/staffSession';

export interface StaffSessionConfig<TInfo extends { token: string }, TValidated> {
  /** Clave de localStorage del token. */
  tokenKey: string;
  /** Clave de localStorage donde se guarda la info completa de la sesión. */
  infoKey: string;
  /** Otras claves a borrar al cerrar la sesión (p. ej. info de sedes legacy). */
  extraKeys?: string[];
  /** Valida el token contra el servidor. Lanza con code 28000 si no sirve. */
  validate: (token: string) => Promise<TValidated>;
  /** Combina lo que devuelve el servidor con lo guardado (slug, sedes…). */
  merge: (token: string, validated: TValidated, cached: TInfo | null) => TInfo;
}

export interface StaffSessionStore<TInfo> {
  info: TInfo | null;
  /** Sólo el arranque. Nunca vuelve a `true` por volver a primer plano. */
  isLoading: boolean;
  /** El arranque no pudo validar por red y no había nada guardado. */
  connectionError: boolean;
  /** Guarda la sesión recién obtenida en el login. */
  setFromLogin: (info: TInfo) => void;
  /** Borra token + info (logout o sesión rechazada). */
  clear: () => void;
  /** Reintento manual desde la pantalla de "sin conexión". */
  retry: () => Promise<void>;
  /** Re-validación silenciosa (no toca `isLoading`). */
  revalidate: () => Promise<void>;
}

const readToken = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const readJson = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch { return null; }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch { /* noop: modo privado / storage lleno */ }
};

export function useStaffSessionStore<TInfo extends { token: string }, TValidated>(
  cfg: StaffSessionConfig<TInfo, TValidated>,
): StaffSessionStore<TInfo> {
  const [info, setInfo] = useState<TInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const infoRef = useRef<TInfo | null>(null);
  const lastValidatedAt = useRef<number | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);

  const apply = useCallback((next: TInfo | null) => {
    infoRef.current = next;
    setInfo(next);
    writeJson(cfg.infoKey, next);
  }, [cfg]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(cfg.tokenKey); } catch { /* noop */ }
    for (const k of cfg.extraKeys ?? []) {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    }
    lastValidatedAt.current = null;
    apply(null);
  }, [cfg, apply]);

  const validateStored = useCallback((mode: 'boot' | 'silent'): Promise<void> => {
    // Un solo pedido a la vez: volver a la app dispara `visibilitychange` y
    // `online` casi juntos.
    if (inFlight.current) return inFlight.current;
    const run = (async () => {
      const token = readToken(cfg.tokenKey);
      if (!token) { apply(null); return; }
      const current = infoRef.current;
      const stored = readJson<TInfo>(cfg.infoKey);
      const cached = current?.token === token ? current : (stored?.token === token ? stored : null);
      try {
        const validated = await cfg.validate(token);
        lastValidatedAt.current = Date.now();
        setConnectionError(false);
        const next = cfg.merge(token, validated, cached);
        // Sin cambios → sin re-render de todo el árbol.
        if (!infoRef.current || JSON.stringify(infoRef.current) !== JSON.stringify(next)) apply(next);
      } catch (e) {
        if (isStaffAuthError(e)) { clear(); return; }
        // Red, timeout, 5xx: la sesión puede seguir viva. No se toca el token.
        if (mode === 'boot') {
          if (cached) apply(cached);
          else setConnectionError(true);
        }
      }
    })().finally(() => { inFlight.current = null; });
    inFlight.current = run;
    return run;
  }, [cfg, apply, clear]);

  // Arranque.
  useEffect(() => {
    let alive = true;
    validateStored('boot').finally(() => { if (alive) setIsLoading(false); });
    return () => { alive = false; };
  }, [validateStored]);

  // Volver a primer plano / recuperar la conexión: re-validación silenciosa y
  // espaciada. Nunca desmonta nada.
  useEffect(() => {
    const onResume = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (!infoRef.current) return;
      if (!shouldRevalidateStaffSession(lastValidatedAt.current, Date.now())) return;
      void validateStored('silent');
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('online', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('online', onResume);
    };
  }, [validateStored]);

  const setFromLogin = useCallback((next: TInfo) => {
    try { localStorage.setItem(cfg.tokenKey, next.token); } catch { /* noop */ }
    lastValidatedAt.current = Date.now();
    setConnectionError(false);
    apply(next);
  }, [cfg, apply]);

  const retry = useCallback(async () => {
    setIsLoading(true);
    try { await validateStored('boot'); } finally { setIsLoading(false); }
  }, [validateStored]);

  const revalidate = useCallback(() => validateStored('silent'), [validateStored]);

  return { info, isLoading, connectionError, setFromLogin, clear, retry, revalidate };
}
